// src/hooks/useLogout.js
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/context/AuthContext'

/**
 * Shared logout handler — single source of truth for logout UX.
 * Used by the desktop sidebar footer dropdown, the mobile avatar menu,
 * and anywhere else we need to sign the user out.
 *
 * Returns an async function that logs out and redirects to /login.
 * Shows a toast on failure.
 */
export default function useLogout() {
  const { logout } = useAuth()
  const navigate = useNavigate()
  return async () => {
    try {
      await logout()
      // Clear per-session UI state so the next sign-in (in this same
      // tab) starts clean. Without this:
      //   - 'scrimly.dashboardSeen' (Home.jsx smart bypass) leaks across
      //     user sessions, breaking the post-login bypass.
      //   - 'postLoginRedirect' (RequireAuth deep-link preservation) gets
      //     set when the auth state flip causes RequireAuth to fire on
      //     the current protected route just before logout completes.
      //     The user's intent at logout is "log out", not "remember this
      //     page for next sign-in" — restoring the page they were on
      //     before logout is wrong. Deep-link preservation still works
      //     for unauth users hitting protected routes (RequireAuth re-
      //     populates the key when that genuinely happens).
      try {
        sessionStorage.removeItem('scrimly.dashboardSeen')
        sessionStorage.removeItem('postLoginRedirect')
      } catch {}
      navigate('/login', { replace: true })
    } catch (err) {
      console.error('Logout failed:', err)
      toast.error('Logout failed. Please try again.')
    }
  }
}
